'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListIcon from '@mui/icons-material/List';
import TodayIcon from '@mui/icons-material/Today';
import { useAppContext } from '../../contexts/app';
import SidebarGroups from './sidebar-groups';
import SidebarDiscord from './sidebar-discord';
import SidebarChips from './sidebar-chips';
import MiniCalendar from './mini-calendar';
import BackToToday from './back-to-today';
import JumpToDateChip from './jump-to-date-chip';

export type NavItem = {
  label: string;
  href: string;
  authRequired?: boolean;
  icon?: React.ReactNode;
};

const allNavItems: NavItem[] = [
  { label: 'Upcoming', href: '/calendar', icon: <ListIcon /> },
  { label: 'Calendar', href: '/calendar/grid', icon: <TodayIcon /> },
  { label: 'Recently Added', href: '/calendar?filter=recent' },
  { label: 'My RSVPs', href: '/calendar?filter=rsvp', authRequired: true },
  { label: 'Hosting', href: '/calendar?filter=managed', authRequired: true },
  { label: 'Drafts', href: '/calendar?filter=drafts', authRequired: true },
  { label: 'Past', href: '/calendar?filter=past' },
];

function useActiveHref(view: 'list' | 'grid'): string {
  const searchParams = useSearchParams();
  const filter = searchParams.get('filter');

  if (filter) {
    return `/calendar?filter=${filter}`;
  }
  if (view === 'grid') {
    return '/calendar/grid';
  }
  return '/calendar';
}

const miniCalendarFilters = new Set([undefined, 'upcoming', 'rsvp', 'managed']);

const CalendarSidebar = ({ cityId, view }: { cityId?: string; view: 'list' | 'grid' }) => {
  const { isLoggedIn } = useAppContext();
  const activeHref = useActiveHref(view);
  const searchParams = useSearchParams();
  const filter = searchParams.get('filter') ?? undefined;

  const showMiniCalendar = cityId
    && view !== 'grid'
    && miniCalendarFilters.has(filter);

  const navItems = useMemo(() => {
    return allNavItems.filter((item) => !item.authRequired || isLoggedIn);
  }, [isLoggedIn]);

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <SidebarChips items={navItems} activeHref={activeHref}>
          {showMiniCalendar && <JumpToDateChip cityId={cityId!} />}
        </SidebarChips>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Paper variant="outlined" sx={{ border: '1px solid', borderColor: 'divider' }}>
          <List component="nav" dense>
            {navItems.map((item) => (
              <ListItemButton
                key={item.href}
                component={Link}
                href={item.href}
                selected={item.href === activeHref}
                dense
              >
                {item.icon && <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          {showMiniCalendar && (
            <>
              <Divider />
              <MiniCalendar key={searchParams.get('start') ?? 'today'} cityId={cityId} />
              <BackToToday />
            </>
          )}
          <SidebarGroups />
          <SidebarDiscord />
        </Paper>
      </Box>
    </>
  );
};

export default CalendarSidebar;
